/**
 * @file Pre-Auth Import Storage
 * @description LocalStorage utilities for pre-auth instrument import
 * Allows users to import and preview instruments before creating an account
 */

const STORAGE_KEY = 'scopax:preAuthImport';
const TTL_HOURS = 24;
const MAX_INSTRUMENTS = 500;

export interface PreAuthInstrument {
  assetId: string;
  name: string;
  lastCalibration: string | null;
  nextDue: string | null;
  location: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
}

export interface PreAuthData {
  instruments: PreAuthInstrument[];
  columnMapping: Record<string, string>;
  fileName: string;
  fileHash: string;
  importedAt: string;
  expiresAt: string;
  version: 1;
}

export type PreAuthClearReason = 'staged' | 'import_completed';

export function shouldClearPreAuthData(reason: PreAuthClearReason): boolean {
  return reason === 'import_completed';
}

/**
 * Generate a simple hash for the file content
 */
export function generateFileHash(content: string): string {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Save pre-auth import data to localStorage
 */
export function savePreAuthImport(data: Omit<PreAuthData, 'expiresAt' | 'version'>): void {
  const expiresAt = new Date(Date.now() + TTL_HOURS * 60 * 60 * 1000).toISOString();
  const limited: PreAuthData = {
    ...data,
    instruments: data.instruments.slice(0, MAX_INSTRUMENTS),
    version: 1,
    expiresAt,
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
  } catch {
    // localStorage quota exceeded or blocked; fail gracefully
    // Silent fail - this is expected in some environments
  }
}

/**
 * Get pre-auth import data from localStorage
 */
export function getPreAuthImport(): PreAuthData | null {
  if (typeof window === 'undefined') return null;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;

  try {
    const data = JSON.parse(stored) as PreAuthData;

    // Check if data has expired
    if (new Date(data.expiresAt) < new Date()) {
      clearPreAuthImport();
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

/**
 * Clear pre-auth import data from localStorage
 */
export function clearPreAuthImport(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore errors when clearing
  }
}

/**
 * Check if there is pre-auth import data
 */
export function hasPreAuthImport(): boolean {
  return getPreAuthImport() !== null;
}

/**
 * Get time remaining until data expires
 */
export function getTimeRemaining(): string | null {
  const data = getPreAuthImport();
  if (!data) return null;

  const remaining = new Date(data.expiresAt).getTime() - Date.now();
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  if (hours > 0) return `${hours} hours`;

  const minutes = Math.floor(remaining / (1000 * 60));
  if (minutes > 0) return `${minutes} minutes`;

  return 'less than a minute';
}

/**
 * Calculate stats from pre-auth instruments
 */
export function calculatePreAuthStats(instruments: PreAuthInstrument[]): {
  total: number;
  overdue: number;
  dueSoon: number;
  valid: number;
  readinessScore: number;
} {
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  let overdue = 0;
  let dueSoon = 0;
  let valid = 0;
  let withValidDates = 0;

  instruments.forEach((inst) => {
    if (!inst.nextDue) return;

    const dueDate = new Date(inst.nextDue);
    withValidDates++;

    if (dueDate < now) {
      overdue++;
    } else if (dueDate < thirtyDaysFromNow) {
      dueSoon++;
    } else {
      valid++;
    }
  });

  // Readiness score: percentage of instruments with valid (non-overdue) status
  const readinessScore = withValidDates > 0
    ? Math.round(((valid + dueSoon) / withValidDates) * 100)
    : 0;

  return {
    total: instruments.length,
    overdue,
    dueSoon,
    valid,
    readinessScore,
  };
}

/**
 * Convert pre-auth instruments to bulk import format
 */
export function convertToImportPayload(instruments: PreAuthInstrument[]): Array<{
  asset_id: string;
  name: string;
  last_calibration_date: string | null;
  next_calibration_due: string | null;
  location: string | null;
  manufacturer: string | null;
  model: string | null;
  serial_number: string | null;
}> {
  return instruments.map((inst) => ({
    asset_id: inst.assetId,
    name: inst.name,
    last_calibration_date: inst.lastCalibration,
    next_calibration_due: inst.nextDue,
    location: inst.location,
    manufacturer: inst.manufacturer,
    model: inst.model,
    serial_number: inst.serialNumber,
  }));
}
